import { useEffect } from 'react';

import { useBlocker } from '@tanstack/react-router';

import { useConfirmDialogStore } from '../stores/confirm-dialog-store';

type Props = {
    title: string;
    description: string;
    disabled?: boolean;
    allowed?: string[];
    onConfirmLeave?: () => Promise<void>;
};

export function useNavigationBlocker({
    title,
    description,
    disabled = false,
    allowed = [],
    onConfirmLeave,
}: Props) {
    const { proceed, reset, status } = useBlocker({
        shouldBlockFn: ({ next }) =>
            !disabled &&
            !allowed.some(
                (route) => next.pathname === route || next.pathname.startsWith(route + '/'),
            ),
        disabled,
        withResolver: true,
    });

    const openConfirmDialog = useConfirmDialogStore((state) => state.open);

    useEffect(() => {
        if (status !== 'blocked') {
            return;
        }

        openConfirmDialog({
            title,
            description,
            confirmText: 'Leave',
            cancelText: 'Stay',
            variant: 'destructive',
            onConfirm: () => {
                if (onConfirmLeave) {
                    return onConfirmLeave().then(proceed);
                } else {
                    proceed();
                    return Promise.resolve();
                }
            },
            onCancel: reset,
        });
    }, [status, onConfirmLeave]);
}
